import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { CompanyDocument } from '../lib/types.ts';

export function AdminDocuments() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });

  const [uploadOpen, setUploadOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: Partial<CompanyDocument> }) =>
      adminApi.patch(`/api/documents/${input.id}`, input.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });

  if (isLoading) return <CenteredSpinner />;
  const documents = data?.documents ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Documents</h1>
          <p className="mt-1 text-sm text-slate-500">
            {documents.length} document{documents.length === 1 ? '' : 's'}
          </p>
        </div>
        <Btn onClick={() => setUploadOpen(true)}>+ Upload document</Btn>
      </div>

      {documents.length === 0 ? (
        <EmptyState
          title="No documents yet"
          description="Upload safety policies that companies can adopt during the questionnaire."
          action={<Btn onClick={() => setUploadOpen(true)}>+ Upload document</Btn>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Version</th>
                <th className="px-4 py-2">Adoptable</th>
                <th className="px-4 py-2">Current</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {documents.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">{d.name}</td>
                  <td className="px-4 py-2 text-slate-700">{d.category}</td>
                  <td className="px-4 py-2 text-slate-700">{d.version}</td>
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={d.is_adoptable}
                      onChange={(e) =>
                        updateMutation.mutate({ id: d.id, patch: { is_adoptable: e.target.checked } })
                      }
                    />
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {d.current_version ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">current</span>
                    ) : (
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() =>
                          updateMutation.mutate({ id: d.id, patch: { current_version: true } })
                        }
                      >
                        promote
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="mr-2 text-xs text-blue-600 hover:underline"
                      onClick={async () => {
                        const res = await adminApi.get<{ url: string }>(
                          `/api/documents/${d.id}/download-url`,
                        ).catch(() => null);
                        if (res?.url) window.open(res.url, '_blank', 'noopener');
                      }}
                    >
                      Download
                    </button>
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => {
                        if (confirm(`Delete ${d.name}?`)) deleteMutation.mutate(d.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(deleteMutation.error || updateMutation.error) && (
        <ErrorMessage error={deleteMutation.error ?? updateMutation.error} />
      )}

      <UploadDocumentModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}

function UploadDocumentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState({ name: '', category: '', version: '1.0', is_adoptable: true });
  const [step, setStep] = useState<'idle' | 'uploading' | 'registering'>('idle');
  const [error, setError] = useState<unknown>(null);

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      setStep('uploading');
      const presigned = await adminApi.post<{ path: string; url: string }>(
        '/api/documents/upload-url',
        { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      setStep('registering');
      await adminApi.post('/api/documents', {
        name: meta.name || file.name,
        category: meta.category || 'General',
        version: meta.version || '1.0',
        file_path: presigned.path,
        is_adoptable: meta.is_adoptable,
      });
      await qc.invalidateQueries({ queryKey: ['documents'] });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setStep('idle');
    }
  }

  function reset() {
    setFile(null);
    setMeta({ name: '', category: '', version: '1.0', is_adoptable: true });
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (step === 'idle') {
          reset();
          onClose();
        }
      }}
      title="Upload document"
      footer={
        <>
          <Btn variant="ghost" onClick={() => { reset(); onClose(); }} disabled={step !== 'idle'}>Cancel</Btn>
          <Btn onClick={submit} loading={step !== 'idle'} disabled={!file || !meta.name}>
            Upload
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <FileDrop
          multiple={false}
          onFiles={(files) => {
            const f = files.item(0);
            if (f) {
              setFile(f);
              setMeta((m) => ({ ...m, name: m.name || f.name.replace(/\.[^.]+$/, '') }));
            }
          }}
          hint="PDF, DOCX, or any policy document"
        />
        {file && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {file.name} ({Math.round(file.size / 1024)} KB)
          </div>
        )}
        <Field label="Name" required>
          <TextInput value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category" required>
            <TextInput value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} placeholder="e.g. Safety" />
          </Field>
          <Field label="Version" required>
            <TextInput value={meta.version} onChange={(e) => setMeta({ ...meta, version: e.target.value })} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={meta.is_adoptable} onChange={(e) => setMeta({ ...meta, is_adoptable: e.target.checked })} />
          Allow companies to adopt this policy
        </label>
        {step === 'uploading' && <div className="text-xs text-slate-500">Uploading file…</div>}
        {step === 'registering' && <div className="text-xs text-slate-500">Recording document…</div>}
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
